import { z } from 'zod';
import { boundedMultiLanguageContentSchema, multiLanguageContentSchema } from './i18n.js';
import { locationSettingsViewSchema, organizationOverridePolicySchema } from './overridable-settings.js';
/**
 * LOWERCASE `#rrggbb`, SIX DIGITS, NOTHING ELSE. No `#abc` shorthand (two
 * spellings of one colour is two things to compare), no 8-digit `#rrggbbaa`
 * (a translucent accent has no defined contrast), no `rgb()`, no named colour.
 * One canonical spelling means an equality check is a valid comparison in
 * SQL, in a test and in Dart.
 */
export const ACCENT_COLOR_PATTERN = /^#[0-9a-f]{6}$/;
export const accentColorSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(ACCENT_COLOR_PATTERN, 'ACCENT_COLOR_INVALID');
/**
 * The only two values `onAccentForeground` may return. These are NOT design
 * tokens and this is NOT a violation of "no raw colors" (CLAUDE.md #7,
 * docs/010): pure black and pure white are the two endpoints of the sRGB
 * lightness axis, they are the two literals `scripts/theme-check.mjs` itself
 * allows, and this file is a data contract rather than a component.
 */
export const ON_ACCENT_FOREGROUNDS = ['#000000', '#ffffff'];
export const onAccentForegroundSchema = z.enum(ON_ACCENT_FOREGROUNDS);
/**
 * WCAG 2.1 relative luminance of a single linearized sRGB channel value
 * (0..1). Values at or below 0.03928 are treated as a straight line per the
 * spec; above that, the standard gamma-expansion curve applies.
 */
function linearizeChannel(channel) {
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}
/** WCAG 2.1 relative luminance of a `#rrggbb` colour. Per-channel sRGB
 *  linearization — never a naive channel average. Throws on a string that
 *  `accentColorSchema` would reject, so callers validate first. */
export function relativeLuminance(hex) {
    if (!ACCENT_COLOR_PATTERN.test(hex)) {
        throw new RangeError(`relativeLuminance: not a canonical #rrggbb colour: ${hex}`);
    }
    const r = linearizeChannel(parseInt(hex.slice(1, 3), 16) / 255);
    const g = linearizeChannel(parseInt(hex.slice(3, 5), 16) / 255);
    const b = linearizeChannel(parseInt(hex.slice(5, 7), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.1 contrast ratio between two `#rrggbb` colours, `(L1+0.05)/(L2+0.05)`
 *  with the lighter first. Always >= 1. */
export function contrastRatio(a, b) {
    const luminanceA = relativeLuminance(a);
    const luminanceB = relativeLuminance(b);
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * Black or white, whichever has the HIGHER contrast against `accentColor`.
 *
 * Because contrast-to-white and contrast-to-black cross at exactly
 * L = sqrt(1.05 * 0.05) - 0.05 = 0.179128..., where both ratios equal
 * ~4.5826:1, the better of the two ALWAYS clears 4.5:1 — for every one of the
 * 16,777,216 possible accents. That is why no accent is ever rejected for
 * contrast, and why docs/010's "contrast-check before applying" guardrail is
 * met by construction. Ties (numerically equal ratios) resolve to '#ffffff'.
 */
export function onAccentForeground(accentColor) {
    const contrastWithWhite = contrastRatio(accentColor, '#ffffff');
    const contrastWithBlack = contrastRatio(accentColor, '#000000');
    return contrastWithBlack > contrastWithWhite ? '#000000' : '#ffffff';
}
/**
 * The pair every surface needs together: what colour, and what goes on it.
 * `null` on BOTH fields means "no accent configured" — the consumer keeps its
 * own default (the kiosk's teal `KioskColors.accent`). The two are never
 * independently null: a foreground without an accent is meaningless.
 */
export const kioskBrandingColorsSchema = z.object({
    accentColor: accentColorSchema.nullable(),
    accentForeground: onAccentForegroundSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Welcome-screen copy (S36 change 6a)                                        */
/* -------------------------------------------------------------------------- */
/**
 * Per-locale ceilings for the two tenant-authored welcome strings. ADMIN INPUT ONLY —
 * see `kioskWelcomeCopySchema` for why neither reaches an outbound schema. Exported so
 * the console can set them as its inputs' `maxLength`. The greeting is a display-size
 * heading on a lobby screen and the CTA a button label; both sized to what fits.
 */
export const KIOSK_WELCOME_GREETING_MAX_CHARS = 120;
export const KIOSK_WELCOME_CHECK_IN_CTA_MAX_CHARS = 40;
/**
 * The tenant's own kiosk welcome copy, on the wire — the SHAPE, none of the bounds: on
 * the way out a rejection would cost the whole payload a lobby screen boots from, so a
 * row written before a ceiling existed must still render.
 *
 * AN OVERRIDE, NOT A REPLACEMENT. `null` — also what a location with no branding row
 * answers — means the kiosk keeps its own i18n default (`welcomeGreeting` /
 * `welcomeCheckInCta` in apps/kiosk/lib/l10n/app_en.arb), which is what lets `/welcome`
 * render unpaired, offline or revoked. LOCATION-ONLY: the organization brand has no copy.
 *
 * `welcomeGreeting` IS PLAIN TEXT. The product default interpolates `{locationName}`;
 * tenant copy deliberately gets no template language. `welcomeLocationLabel` and
 * `welcomeInviteCodeCta` stay product chrome.
 */
export const kioskWelcomeCopySchema = z.object({
    welcomeGreeting: multiLanguageContentSchema.nullable(),
    welcomeCheckInCta: multiLanguageContentSchema.nullable(),
});
/**
 * One scope's kiosk brand as the console sees it: the accent (plus its derived
 * foreground), whether a logo is stored, and the welcome copy. The logo's bytes are
 * fetched separately (`GET /branding/logo` for the organization row,
 * `GET /locations/:locationId/branding/logo` for a location's own row); the file id never
 * leaves the server (docs/008 S7).
 */
export const kioskBrandingValuesSchema = kioskBrandingColorsSchema.merge(kioskWelcomeCopySchema).extend({
    hasLogo: z.boolean(),
});
/** `GET|PUT /api/web/branding` (and the organization logo upload): the organization row. */
export const organizationKioskBrandingDTOSchema = kioskBrandingValuesSchema.merge(organizationOverridePolicySchema);
/**
 * `GET|PUT /api/web/locations/:locationId/branding`, `PUT|DELETE .../branding/override`
 * and the location logo upload: the location view (`locationSettingsViewSchema`). When
 * `source` is `LOCATION` the effective logo is the location's own
 * (`GET /locations/:locationId/branding/logo`); otherwise it is the organization's
 * (`GET /branding/logo`).
 */
export const locationKioskBrandingViewDTOSchema = locationSettingsViewSchema(kioskBrandingValuesSchema);
/** One row of `GET /api/web/branding/locations`. `isCustom` = the location has its own row. */
export const locationBrandingSummaryDTOSchema = z.object({
    locationId: z.string().min(1),
    name: z.string().min(1),
    isCustom: z.boolean(),
});
const welcomeGreetingInputSchema = boundedMultiLanguageContentSchema(KIOSK_WELCOME_GREETING_MAX_CHARS).nullable();
const welcomeCheckInCtaInputSchema = boundedMultiLanguageContentSchema(KIOSK_WELCOME_CHECK_IN_CTA_MAX_CHARS).nullable();
/**
 * `PUT /api/web/locations/:locationId/branding` — edit the location's override (creating
 * it as a copy of the organization brand first when the location still inherits).
 * `accentColor: null` clears the accent. An absent copy key leaves the stored copy alone;
 * `null` clears it. The logo has its own upload/delete routes.
 */
export const updateKioskBrandingSchema = z.object({
    accentColor: accentColorSchema.nullable(),
    welcomeGreeting: welcomeGreetingInputSchema.optional(),
    welcomeCheckInCta: welcomeCheckInCtaInputSchema.optional(),
});
/**
 * `PUT /api/web/branding` — edit the organization row. Every key optional; an absent key
 * leaves the stored value alone, `null` clears it. `allowLocationOverride` is the
 * organization's "locations may customize branding" toggle.
 */
export const updateOrganizationKioskBrandingSchema = z
    .object({
    accentColor: accentColorSchema.nullable().optional(),
    welcomeGreeting: welcomeGreetingInputSchema.optional(),
    welcomeCheckInCta: welcomeCheckInCtaInputSchema.optional(),
    allowLocationOverride: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=kiosk-branding.js.map